import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getSeatMap } from "../../services/api";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import {
  FaTimes,
  FaPlane,
  FaCheck,
  FaChevronLeft,
  FaUser,
  FaRedo,
} from "react-icons/fa";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SeatSelection {
  segmentIndex: number;
  rowNumber: number;
  seatNumber: string;
  passengerIndex: number;
  offerItemId?: string;
  amount?: number;
  currency?: string;
}

export interface SeatMapProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  revalId: string;
  sessionId?: string;
  passengerCounts: { ADT?: number; CHD?: number; INF?: number };
  currency?: string;
  onConfirm?: (selections: SeatSelection[]) => void;
}

interface SeatData {
  rowNumber: number;
  number: string;
  occupied: boolean;
  chargeable: boolean;
  exitRow?: boolean;
  location?: string[];
  facilities?: string[];
  offerItemId?: string;
  amount?: number;
  currency?: string;
  commercialName?: string;
}

interface RowData {
  rowNumber: number;
  seats: SeatData[];
}

interface CabinData {
  firstRow: number;
  lastRow: number;
  rbd?: string;
  marketingDescription?: string;
  rows: RowData[];
}

interface SegmentSeatMap {
  success: boolean;
  error?: string;
  equipment?: string;
  segmentIndex: number;
  flight?: {
    origin: string;
    destination: string;
    departureDate: string;
    marketingCarrier: string;
    marketingFlightNumber: string;
  };
  cabins: CabinData[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveLayout(cabins: CabinData[]): string[][] {
  const letters = new Set<string>();
  for (const cabin of cabins) {
    for (const row of cabin.rows) {
      for (const s of row.seats) {
        letters.add(s.number);
      }
    }
  }
  const sorted = Array.from(letters).sort();
  if (sorted.length === 0) return [[]];

  const groups: string[][] = [];
  let current: string[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1].charCodeAt(0);
    const cur = sorted[i].charCodeAt(0);
    const gap = cur - prev;
    if (gap > 1 && !(prev === "H".charCodeAt(0) && cur === "J".charCodeAt(0))) {
      groups.push(current);
      current = [sorted[i]];
    } else {
      current.push(sorted[i]);
    }
  }
  groups.push(current);

  if (groups.length === 1 && sorted.length >= 6) {
    if (sorted.length === 6) {
      return [sorted.slice(0, 3), sorted.slice(3)];
    }
    if (sorted.length >= 9) {
      return [sorted.slice(0, 3), sorted.slice(3, 7), sorted.slice(7)];
    }
    const mid = Math.ceil(sorted.length / 2);
    return [sorted.slice(0, mid), sorted.slice(mid)];
  }

  return groups;
}

function getCabinLabel(desc?: string): string {
  if (!desc) return "Economy";
  const d = desc.toLowerCase();
  if (d.includes("first")) return "First Class";
  if (d.includes("business")) return "Business";
  if (d.includes("premium") && d.includes("economy")) return "Premium Economy";
  return desc;
}

function formatPrice(amount?: number, currency?: string): string {
  if (amount == null) return "";
  const c = currency || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: c,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${c} ${amount}`;
  }
}

// ---------------------------------------------------------------------------
// Passenger builder (skips INF)
// ---------------------------------------------------------------------------

interface PassengerInfo {
  index: number;
  label: string;
  type: "ADT" | "CHD";
}

function buildPassengerList(counts: {
  ADT?: number;
  CHD?: number;
  INF?: number;
}): PassengerInfo[] {
  const list: PassengerInfo[] = [];
  let idx = 0;
  for (let i = 0; i < (counts.ADT || 0); i++) {
    list.push({ index: idx++, label: `Passenger ${idx} (Adult)`, type: "ADT" });
  }
  for (let i = 0; i < (counts.CHD || 0); i++) {
    list.push({ index: idx++, label: `Passenger ${idx} (Child)`, type: "CHD" });
  }
  return list;
}

// ---------------------------------------------------------------------------
// Seat sub-component
// ---------------------------------------------------------------------------

const Seat: React.FC<{
  seat: SeatData;
  isSelected: boolean;
  selectedLabel?: string;
  onSelect: () => void;
}> = React.memo(({ seat, isSelected, selectedLabel, onSelect }) => {
  const base =
    "relative flex items-center justify-center rounded-t-lg rounded-b-md text-[10px] sm:text-xs font-bold transition-all duration-150 select-none focus:outline-none focus:ring-2 focus:ring-primary/40";

  const sizeClass = "w-[30px] h-[30px] sm:w-[36px] sm:h-[36px]";

  const colorClass = clsx({
    "bg-primary text-white border-2 border-primary shadow-lg scale-[1.06] z-10":
      isSelected,
    "bg-gray-200 border-2 border-gray-300 cursor-not-allowed text-gray-400":
      seat.occupied && !isSelected,
    "bg-white border-2 border-emerald-400 hover:bg-emerald-50 cursor-pointer":
      !seat.occupied && !seat.chargeable && !isSelected,
    "bg-white border-2 border-sky-400 hover:bg-sky-50 cursor-pointer":
      !seat.occupied && seat.chargeable && !isSelected,
  });

  return (
    <div className="flex flex-col items-center">
      <button
        className={clsx(base, sizeClass, colorClass)}
        disabled={seat.occupied}
        onClick={onSelect}
        aria-label={`Seat ${seat.rowNumber}${seat.number}${
          seat.occupied ? " occupied" : ""
        }${seat.amount ? ` ${formatPrice(seat.amount, seat.currency)}` : ""}`}
      >
        {isSelected ? selectedLabel || seat.number : seat.occupied ? "" : seat.number}
      </button>
      {/* Price tag for paid available seats */}
      {!seat.occupied && seat.chargeable && seat.amount != null && !isSelected && (
        <span className="text-[8px] sm:text-[9px] text-sky-600 font-semibold mt-0.5 leading-none whitespace-nowrap">
          {formatPrice(seat.amount, seat.currency)}
        </span>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const SeatMap: React.FC<SeatMapProps> = ({
  isOpen,
  onClose,
  userId,
  revalId,
  sessionId,
  passengerCounts,
  currency,
  onConfirm,
}) => {
  const [seatMaps, setSeatMaps] = useState<SegmentSeatMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSegment, setActiveSegment] = useState(0);
  const [selections, setSelections] = useState<SeatSelection[]>([]);
  const [activePassenger, setActivePassenger] = useState(0);
  const cabinRef = useRef<HTMLDivElement>(null);

  const passengers = useMemo(
    () => buildPassengerList(passengerCounts),
    [passengerCounts]
  );

  const totalPassengers = passengers.length;

  // ---- Body scroll lock ----
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => document.body.classList.remove("overflow-hidden");
  }, [isOpen]);

  // ---- Escape key ----
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // ---- Fetch ----
  const fetchSeatMap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSeatMap({
        userId,
        revalId,
        sessionId,
        passengerCounts,
        currency,
      });
      if (data?.success && data.seatMaps?.length > 0) {
        setSeatMaps(data.seatMaps);
      } else {
        const segError = data?.seatMaps?.[0]?.error;
        const topError = (data as any)?.error;
        setError(
          segError ||
            topError ||
            "Seat map not available for this flight."
        );
      }
    } catch (err: any) {
      const errData = err?.response?.data;
      const nestedError =
        errData?.errors?.[0]?.seatMaps?.[0]?.error ||
        errData?.errors?.[0]?.error ||
        errData?.error;
      setError(nestedError || err.message || "Failed to load seat map.");
    } finally {
      setLoading(false);
    }
  }, [userId, revalId, sessionId, passengerCounts, currency]);

  useEffect(() => {
    if (isOpen) {
      fetchSeatMap();
    }
  }, [isOpen, fetchSeatMap]);

  // ---- Derived data ----
  const successfulSegments = useMemo(
    () => seatMaps.filter((s) => s.success && s.cabins?.length > 0),
    [seatMaps]
  );
  const failedSegments = useMemo(
    () => seatMaps.filter((s) => !s.success || !s.cabins?.length),
    [seatMaps]
  );
  const allFailed =
    !loading && !error && seatMaps.length > 0 && successfulSegments.length === 0;

  const currentMap = successfulSegments[activeSegment] ?? null;
  const seatLayout = useMemo(
    () => (currentMap ? deriveLayout(currentMap.cabins) : []),
    [currentMap]
  );

  // ---- Selection helpers ----
  const getSelectionForPassengerSegment = useCallback(
    (passengerIdx: number, segIdx: number) =>
      selections.find(
        (s) => s.passengerIndex === passengerIdx && s.segmentIndex === segIdx
      ),
    [selections]
  );

  const isSeatSelected = useCallback(
    (segIdx: number, rowNumber: number, seatNumber: string) =>
      selections.some(
        (s) =>
          s.segmentIndex === segIdx &&
          s.rowNumber === rowNumber &&
          s.seatNumber === seatNumber
      ),
    [selections]
  );

  const getSelectionPassengerLabel = useCallback(
    (segIdx: number, rowNumber: number, seatNumber: string) => {
      const sel = selections.find(
        (s) =>
          s.segmentIndex === segIdx &&
          s.rowNumber === rowNumber &&
          s.seatNumber === seatNumber
      );
      if (!sel) return undefined;
      return `P${sel.passengerIndex + 1}`;
    },
    [selections]
  );

  const handleSeatClick = useCallback(
    (segmentIndex: number, seat: SeatData) => {
      if (seat.occupied) return;

      setSelections((prev) => {
        // If this exact seat is already selected by active passenger, deselect
        const existingForThisSeat = prev.find(
          (s) =>
            s.segmentIndex === segmentIndex &&
            s.rowNumber === seat.rowNumber &&
            s.seatNumber === seat.number &&
            s.passengerIndex === activePassenger
        );
        if (existingForThisSeat) {
          return prev.filter((s) => s !== existingForThisSeat);
        }

        // If seat is taken by another passenger, do nothing
        const takenByOther = prev.find(
          (s) =>
            s.segmentIndex === segmentIndex &&
            s.rowNumber === seat.rowNumber &&
            s.seatNumber === seat.number &&
            s.passengerIndex !== activePassenger
        );
        if (takenByOther) return prev;

        // Remove old selection for this passenger + segment
        const withoutOld = prev.filter(
          (s) =>
            !(
              s.passengerIndex === activePassenger &&
              s.segmentIndex === segmentIndex
            )
        );

        const newSel: SeatSelection = {
          segmentIndex,
          rowNumber: seat.rowNumber,
          seatNumber: seat.number,
          passengerIndex: activePassenger,
          offerItemId: seat.offerItemId,
          amount: seat.amount,
          currency: seat.currency,
        };

        // Auto-advance to next passenger without a seat on this segment
        setTimeout(() => {
          setActivePassenger((cur) => {
            for (let i = 1; i <= totalPassengers; i++) {
              const next = (cur + i) % totalPassengers;
              const hasSelection = [...withoutOld, newSel].some(
                (s) =>
                  s.passengerIndex === next && s.segmentIndex === segmentIndex
              );
              if (!hasSelection) return next;
            }
            return cur;
          });
        }, 0);

        return [...withoutOld, newSel];
      });
    },
    [activePassenger, totalPassengers]
  );

  // ---- Summary calculations ----
  const segmentSelections = useMemo(
    () =>
      currentMap
        ? selections.filter((s) => s.segmentIndex === currentMap.segmentIndex)
        : [],
    [selections, currentMap]
  );

  const totalCost = useMemo(
    () => selections.reduce((sum, s) => sum + (s.amount || 0), 0),
    [selections]
  );

  const allPassengersSeated = useMemo(() => {
    if (!currentMap) return false;
    return passengers.every((p) =>
      selections.some(
        (s) =>
          s.passengerIndex === p.index &&
          s.segmentIndex === currentMap.segmentIndex
      )
    );
  }, [selections, currentMap, passengers]);

  const allSegmentsAllSeated = useMemo(() => {
    return successfulSegments.every((seg) =>
      passengers.every((p) =>
        selections.some(
          (s) =>
            s.passengerIndex === p.index &&
            s.segmentIndex === seg.segmentIndex
        )
      )
    );
  }, [selections, successfulSegments, passengers]);

  const summaryText = useMemo(() => {
    if (segmentSelections.length === 0) return "No seats selected";
    return segmentSelections
      .sort((a, b) => a.passengerIndex - b.passengerIndex)
      .map((s) => `${s.rowNumber}${s.seatNumber}`)
      .join(", ");
  }, [segmentSelections]);

  // ---- Do not render anything if closed ----
  // We use AnimatePresence outside

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal panel */}
          <motion.div
            className="relative z-10 w-full h-full sm:h-[92vh] sm:max-w-[560px] sm:rounded-2xl bg-white flex flex-col overflow-hidden shadow-2xl"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            {/* ============================================================ */}
            {/* HEADER */}
            {/* ============================================================ */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={onClose}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <FaChevronLeft className="text-sm" />
                  <span className="font-semibold text-base">Select Seats</span>
                </button>
                <button
                  onClick={onClose}
                  className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
                >
                  Skip
                </button>
              </div>

              {/* Segment tabs */}
              {successfulSegments.length > 1 && (
                <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                  {successfulSegments.map((seg, idx) => {
                    const label = seg.flight
                      ? `${seg.flight.origin} → ${seg.flight.destination}`
                      : `Segment ${idx + 1}`;
                    const isActive = idx === activeSegment;
                    return (
                      <button
                        key={seg.segmentIndex}
                        onClick={() => {
                          setActiveSegment(idx);
                          setActivePassenger(0);
                        }}
                        className={clsx(
                          "flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap",
                          isActive
                            ? "bg-primary text-white shadow-md"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        )}
                      >
                        <FaPlane
                          className={clsx("text-[10px]", !isActive && "opacity-50")}
                        />
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ============================================================ */}
            {/* BODY (scrollable) */}
            {/* ============================================================ */}
            <div className="flex-1 overflow-y-auto overscroll-contain" ref={cabinRef}>
              {/* LOADING */}
              {loading && (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                  <div className="relative w-16 h-16 mb-4">
                    <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
                    <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                    <FaPlane className="absolute inset-0 m-auto text-primary text-lg" />
                  </div>
                  <p className="text-sm text-gray-500 font-medium">
                    Loading seat map...
                  </p>
                </div>
              )}

              {/* ERROR: all segments failed or top-level error */}
              {!loading && (error || allFailed) && (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] px-6 text-center">
                  <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-5">
                    <FaPlane className="text-red-400 text-3xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    Seat map not available for this flight
                  </h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-xs">
                    {error ||
                      "The airline may not support seat selection in this environment."}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={fetchSeatMap}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors text-sm font-semibold shadow"
                    >
                      <FaRedo className="text-xs" />
                      Retry
                    </button>
                    <button
                      onClick={onClose}
                      className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors text-sm font-semibold"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {/* SUCCESS: show seat map */}
              {!loading && !error && !allFailed && currentMap && (
                <div className="pb-4">
                  {/* Warning for partially failed segments */}
                  {failedSegments.length > 0 && (
                    <div className="mx-4 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                      Seat map is not available for{" "}
                      {failedSegments
                        .map((s) =>
                          s.flight
                            ? `${s.flight.origin} → ${s.flight.destination}`
                            : `Segment ${s.segmentIndex + 1}`
                        )
                        .join(", ")}
                      . You can still select seats for available segments.
                    </div>
                  )}

                  {/* ---- Passenger pills ---- */}
                  <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 py-3">
                    <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
                      {passengers.map((p) => {
                        const sel = getSelectionForPassengerSegment(
                          p.index,
                          currentMap.segmentIndex
                        );
                        const isActive = p.index === activePassenger;
                        return (
                          <button
                            key={p.index}
                            onClick={() => setActivePassenger(p.index)}
                            className={clsx(
                              "flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap",
                              isActive
                                ? "bg-primary text-white shadow-md"
                                : sel
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            )}
                          >
                            {sel ? (
                              <FaCheck className="text-[9px]" />
                            ) : (
                              <FaUser className="text-[9px]" />
                            )}
                            {sel
                              ? `${p.label.split(" (")[0]} ${sel.rowNumber}${sel.seatNumber}`
                              : p.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[10px] sm:text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-t-md rounded-b-sm bg-white border-2 border-emerald-400" />
                        <span>Available</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-t-md rounded-b-sm bg-white border-2 border-sky-400" />
                        <span>Paid</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-t-md rounded-b-sm bg-primary border-2 border-primary" />
                        <span>Selected</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-t-md rounded-b-sm bg-gray-200 border-2 border-gray-300" />
                        <span>Occupied</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[8px] font-bold text-red-500 bg-red-50 border border-red-200 px-1 py-0.5 rounded leading-none">
                          EXIT
                        </span>
                        <span>Exit Row</span>
                      </div>
                    </div>
                  </div>

                  {/* ---- Aircraft visualization ---- */}
                  <div className="flex flex-col items-center px-2 pt-4">
                    {/* Fuselage nose */}
                    <div
                      className="bg-gradient-to-b from-gray-200 to-gray-100 rounded-t-[80px] h-14 border-x-2 border-t-2 border-gray-300"
                      style={{
                        width: `${Math.min(
                          seatLayout.flat().length * 42 + 72,
                          420
                        )}px`,
                      }}
                    />

                    {/* Fuselage body */}
                    <div
                      className="border-x-2 border-gray-300 bg-gradient-to-b from-gray-50 via-white to-gray-50 px-3 sm:px-4 py-4 relative"
                      style={{
                        width: `${Math.min(
                          seatLayout.flat().length * 42 + 72,
                          420
                        )}px`,
                      }}
                    >
                      {/* Column headers */}
                      <div className="flex items-center justify-center gap-0 mb-3 sticky top-0 z-10 bg-white/80 backdrop-blur-sm py-1 -mx-3 sm:-mx-4 px-3 sm:px-4">
                        <div className="w-7 sm:w-9" />
                        {seatLayout.map((group, gi) => (
                          <React.Fragment key={gi}>
                            {gi > 0 && (
                              <div className="w-6 flex items-center justify-center">
                                <div className="w-px h-4 bg-gray-300" />
                              </div>
                            )}
                            <div className="flex gap-1">
                              {group.map((letter) => (
                                <div
                                  key={letter}
                                  className="w-[30px] sm:w-[36px] text-center text-[10px] text-gray-400 font-bold"
                                >
                                  {letter}
                                </div>
                              ))}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Cabins */}
                      {currentMap.cabins.map((cabin, ci) => {
                        const cabinLabel = getCabinLabel(
                          cabin.marketingDescription
                        );
                        return (
                          <div key={ci}>
                            {/* Cabin divider */}
                            <div className="flex items-center gap-2 my-3">
                              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                              <span className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider px-3 py-1 bg-gray-50 rounded-full border border-gray-200">
                                {cabinLabel}
                              </span>
                              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                            </div>

                            {/* Rows */}
                            {cabin.rows.map((row) => {
                              const isExitRow = row.seats.some(
                                (s) => s.exitRow
                              );
                              return (
                                <div
                                  key={row.rowNumber}
                                  className="relative mb-1"
                                >
                                  {/* Exit row markers */}
                                  {isExitRow && (
                                    <>
                                      <div className="absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-full z-10">
                                        <span className="text-[7px] font-bold text-red-500 bg-red-50 border border-red-200 px-1 py-0.5 rounded">
                                          EXIT
                                        </span>
                                      </div>
                                      <div className="absolute -right-1 top-1/2 -translate-y-1/2 translate-x-full z-10">
                                        <span className="text-[7px] font-bold text-red-500 bg-red-50 border border-red-200 px-1 py-0.5 rounded">
                                          EXIT
                                        </span>
                                      </div>
                                      <div className="absolute inset-x-0 top-0 border-t border-dashed border-red-300" />
                                    </>
                                  )}

                                  <div className="flex items-center justify-center gap-0">
                                    {/* Row number */}
                                    <div className="w-7 sm:w-9 text-right pr-1.5">
                                      <span className="text-[10px] text-gray-400 font-mono font-medium">
                                        {row.rowNumber}
                                      </span>
                                    </div>

                                    {/* Seats grouped by aisle */}
                                    {seatLayout.map((group, gi) => (
                                      <React.Fragment key={gi}>
                                        {gi > 0 && (
                                          <div className="w-6" />
                                        )}
                                        <div className="flex gap-1">
                                          {group.map((letter) => {
                                            const seat = row.seats.find(
                                              (s) => s.number === letter
                                            );
                                            if (!seat) {
                                              return (
                                                <div
                                                  key={letter}
                                                  className="w-[30px] h-[30px] sm:w-[36px] sm:h-[36px]"
                                                />
                                              );
                                            }
                                            const selected = isSeatSelected(
                                              currentMap.segmentIndex,
                                              row.rowNumber,
                                              letter
                                            );
                                            return (
                                              <Seat
                                                key={letter}
                                                seat={seat}
                                                isSelected={selected}
                                                selectedLabel={getSelectionPassengerLabel(
                                                  currentMap.segmentIndex,
                                                  row.rowNumber,
                                                  letter
                                                )}
                                                onSelect={() =>
                                                  handleSeatClick(
                                                    currentMap.segmentIndex,
                                                    seat
                                                  )
                                                }
                                              />
                                            );
                                          })}
                                        </div>
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>

                    {/* Fuselage tail */}
                    <div
                      className="bg-gradient-to-t from-gray-200 to-gray-100 rounded-b-[40px] h-8 border-x-2 border-b-2 border-gray-300"
                      style={{
                        width: `${Math.min(
                          seatLayout.flat().length * 42 + 72,
                          420
                        )}px`,
                      }}
                    />

                    {/* Bottom spacer for footer */}
                    <div className="h-6" />
                  </div>
                </div>
              )}
            </div>

            {/* ============================================================ */}
            {/* FOOTER */}
            {/* ============================================================ */}
            {!loading && !error && !allFailed && currentMap && (
              <div className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  {/* Left: summary */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 truncate">
                      {summaryText}
                    </p>
                    {totalCost > 0 && (
                      <p className="text-sm font-bold text-gray-800">
                        {formatPrice(
                          totalCost,
                          selections.find((s) => s.currency)?.currency ||
                            currency
                        )}
                      </p>
                    )}
                  </div>

                  {/* Right: buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onClose}
                      className="px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Skip
                    </button>
                    <button
                      onClick={() => {
                        onConfirm?.(selections);
                        onClose();
                      }}
                      disabled={selections.length === 0}
                      className={clsx(
                        "px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all shadow-md",
                        selections.length === 0
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-primary hover:bg-primary/90",
                        allSegmentsAllSeated && selections.length > 0 &&
                          "animate-pulse"
                      )}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SeatMap;
